<template>
  <aha-modal
    @hide="$emit('hide')"
    :title="$i18nPresenter.t('Share Report')"
    classes="overflow-visible"
  >
    <div class="flex flex-col gap-4">
      <aha-info-box>
        {{ $i18nPresenter.t('Anyone on the Internet with the link can view') }}
      </aha-info-box>
      <div class="flex gap-2 items-center" v-if="!isLoading">
        <aha-input
          :disabled="isResetting"
          class="flex-1"
          readonly
          v-model="sharedReportLink"
        />
      </div>
      <aha-skeleton-loader
        v-else
        width="100%"
        height="40px"
      />
    </div>
    <template #action>
      <div class="flex gap-2" v-if="!isLoading">
        <aha-antd-button
          name="reset-shared-report-link-button"
          v-aha-emit-action
          :submitting="isResetting"
          icon-name="aha-arrows-clock-wise"
          icon-view-box="0 0 16 16"
          icon-stroke-color
          icon-maintain-color
          variant="secondary"
          @click="resetLink"
          aria-label="Reset link - Create a new link, the current link will be inaccessible"
        >
          {{ $i18nPresenter.t('Reset link') }}
        </aha-antd-button>
        <aha-antd-button
          name="copy-shared-report-link-button"
          icon-name="link-icon"
          icon-view-box="0 0 18 14"
          :disabled="isResetting"
          v-aha-emit-action
          variant="primary"
          @click="copyLink"
        >
          {{ $i18nPresenter.t('Copy link') }}
        </aha-antd-button>
      </div>
    </template>
  </aha-modal>
</template>
<script>
import * as Sentry from '@sentry/vue'
import { mapActions } from 'vuex'
import { ROUTE_NAMES } from '@/constant/routes'
import config from '@/config'
import Utils from '@/util'

export default {
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      reportToken: null,
      isLoading: false,
      isResetting: false,
      popperOptions: {
        modifiers: {
          preventOverflow: {
            escapeWithReference: true,
          },
        },
      },
    }
  },
  computed: {
    sharedReportLink() {
      const link = this.$router.resolve({
        name: ROUTE_NAMES.SHARED_REPORT,
        params: {
          id: this.reportToken,
        },
      })

      return `${config.presenterDomain}${link.href}`
    },
  },
  async mounted() {
    if (!this.presentation?.id) return
    await this.loadToken()
  },
  methods: {
    ...mapActions('presentationReport', ['getSharedReportTokenAction', 'resetSharedReportTokenAction']),
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.sharedReportLink)
        Utils.showToastSuccess(this.$i18nPresenter.t('Link copied!'))
      } catch (error) {
        Sentry.captureMessage('Copy shared report link failed!', {
          level: 'warning',
          tags: { component: 'ShareReportLinkModal' },
          extra: {
            presentationId: this.presentation?.id,
          },
        })
      }
    },
    async loadToken() {
      try {
        this.isLoading = true
        this.reportToken = await this.getSharedReportTokenAction(this.presentation?.id)
      } catch (error) {
        Sentry.captureMessage('Copy shared report link failed!', {
          level: 'warning',
          tags: { component: 'ShareReportLinkModal' },
          extra: {
            presentationId: this.presentation?.id,
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    async resetLink() {
      try {
        this.isResetting = true
        this.reportToken = await this.resetSharedReportTokenAction(this.presentation?.id)
        Utils.showToastSuccess(this.$i18nPresenter.t('New link created. The old link no longer works.'))
      } catch (error) {
        Sentry.captureMessage('Reset shared report link failed!', {
          level: 'warning',
          tags: { component: 'ShareReportLinkModal' },
          extra: {
            presentationId: this.presentation?.id,
          },
        })
      } finally {
        this.isResetting = false
      }
    },
  },
}
</script>
